import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import { Dictionary } from 'underscore';

import { Location } from '~commonInfra/utils/Location';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { openReactAlertDialog } from '~src/commonInfra/dialogs/ReactAlertDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ExplorerStyles } from '~src/spacesPlatform/components/Explorer/ExplorerStyles';

import { IAuthenticationProvider, is3rdParty, SiteAuthenticationSettingsService } from '../../services/SiteAuthenticationSettingsService';
import { ActionsMenuItem, AuthenticationActionsMenu } from './AuthenticationActionsMenu';
import { AuthenticationColumnKeys } from './AuthenticationTypesGrid';

export interface AuthenticationActionCellOptions {
  getProviderInfo: (row: DataGrid.Row) => Dictionary<any>;
  disableAuthenticationProvider: () => Promise<void>;
  refreshGrid: () => void;
  refreshAuthentication: () => void;
  enabledSAMLConfigForSCIM: string;
  refreshEmbeddedProviders: () => void;
}

function toggleProviderEnabled(provider: IAuthenticationProvider, enabledSAMLConfigForSCIM: string): Promise<any> {
  if (provider.isEnabled) {
    if (provider.id === enabledSAMLConfigForSCIM) {
      return SCIMCannotBeEnabledDialog();
    }
    return disableProviderWithConfirmation(provider);
  } else {
    return SiteAuthenticationSettingsService.setProviderEnabled(provider.idpConfigurationId, true).then((result: IResult) => {
      if (result && result.errors && result.errors.length > 0) {
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
      } else {
          // ToasterService.instance.pop({ type: ToastType.Success, text: '{Success Message}' });
      }
    });
  }
}

function toggleProviderDelete(provider: IAuthenticationProvider, enabledSAMLConfigForSCIM: string): Promise<any> {
    if (provider.id === enabledSAMLConfigForSCIM) {
      return SCIMCannotBeDeletedDialog();
    }
    return deleteProviderWithConfirmation(provider);
}

function SCIMCannotBeEnabledDialog(): Promise<any> {
  const dialogInstance = openReactAlertDialog({
    title: Messages.siteAuthenticationSCIMSAMLCannotDisable(),
    message: Messages.siteAuthenticationSCIMSAMLCannotDisableMessage(),
    dialogWidth: TabDialogWidth.width375px,
  });

  return Promise.resolve(dialogInstance.result);
}

function SCIMCannotBeDeletedDialog(): Promise<any> {
  const dialogInstance = openReactAlertDialog({
    title: Messages.siteAuthenticationSCIMSAMLCannotDelete(),
    message: Messages.siteAuthenticationSCIMSAMLCannotDeleteMessage(),
    dialogWidth: TabDialogWidth.width375px,
  });

  return Promise.resolve(dialogInstance.result);
}

function disableProviderWithConfirmation(provider: IAuthenticationProvider): Promise<any> {
  const dialogInstance = openReactActionDialog({
    title: Messages.siteAuthenticationDisable(),
    bodyComponent: () => disableAuthenticationProviderBody(provider),
    initialBodyProps: {},
    initialDialogState: {},
    actionName: Messages.siteAuthenticationDisable(),
    action: () => {
      return SiteAuthenticationSettingsService.setProviderEnabled(provider.idpConfigurationId, false).then((result: IResult) => {
        if (result && result.errors && result.errors.length > 0) {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
        } else {
            // ToasterService.instance.pop({ type: ToastType.Success, text: '' });
        }
      });
    },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: true
  });

  return Promise.resolve(dialogInstance.result);
}

function deleteProviderWithConfirmation(provider: IAuthenticationProvider): Promise<any> {
  const dialogInstance = openReactActionDialog({
    title: Messages.siteAuthenticationDelete(),
    bodyComponent: () => deleteAuthenticationProviderBody(provider),
    initialBodyProps: {},
    initialDialogState: {},
    actionName: Messages.siteAuthenticationDelete(),
    action: () => {
      return SiteAuthenticationSettingsService.deleteProvider(provider).then(() => {
        ToasterService.instance.pop({ type: ToastType.Success, text: Messages.siteAuthenticationDeleteSuccess({ CONFIG_NAME: provider.idpConfigurationName }) });
      }).catch(() => {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
      });
    },
    isActionDelete: true,
    dialogWidth: TabDialogWidth.width375px,
    dismissDialogOnActionFailed: true
  });

  return Promise.resolve(dialogInstance.result);
}

function deleteAuthenticationProviderBody(provider: IAuthenticationProvider) {
  return (
      <>
          <p>{Messages.siteAuthenticationDeleteDialogue1({ PROVIDER_NAME: provider.idpConfigurationName })}</p>
          <p>{Messages.siteAuthenticationDeleteDialogue2()}</p>
          <p></p>
      </>
  );
}

function disableAuthenticationProviderBody(provider: IAuthenticationProvider) {
  return (
    <>
        <p>{Messages.siteAuthenticationDisableConfigurationDesc1({ CONFIG_NAME: provider.idpConfigurationName })}</p>
        <p>{Messages.siteAuthenticationDisableConfigurationDesc2()}</p>
        <p></p>
    </>
);
}

export function AuthenticationActionsMenuCell(
    options: AuthenticationActionCellOptions,
    cellOverrides: React.CSSProperties = {}
): React.Factory<BodyCell.Props> {
  return React.createFactory(class extends React.Component<BodyCell.Props, {}> {

    render() {
      const provider = this.props.row.data[AuthenticationColumnKeys.AuthenticationProvider] as IAuthenticationProvider;
      const actionsMenuDisabled = !is3rdParty(provider.id);
      const actions: ActionsMenuItem[] = actionsMenuDisabled ? [] : [
        {
          // Disable... / Enable
          label: provider.isEnabled ? Messages.siteAuthenticationDisableAction() : Messages.siteAuthenticationEnableAction(),
          execute: () => { return toggleProviderEnabled(provider, options.enabledSAMLConfigForSCIM).then(() => {
              options.refreshGrid();
              options.refreshAuthentication();
              options.refreshEmbeddedProviders();
            });
          },
        },
        {
          // Edit...
          label: Messages.siteAuthenticationEditAction(),
          execute: () => {
            // HOW WILL WE DISPLAY THE EDIT CONFIG BUTTON???
            const urls = siteUrlsSelector(getAppStore().getState());
            const authUrl = urls.authenticationEditConfigurationUrl(provider.idpConfigurationId);
            Location.instance.setUrl(urls.authenticationEditConfigurationUrl(provider.idpConfigurationId));
            return Promise.resolve();
          }
        },
        {
          // Delete...
          label: Messages.siteAuthenticationDeleteAction(),
          execute: () => { return toggleProviderDelete(provider, options.enabledSAMLConfigForSCIM).then(() => {
              options.refreshGrid();
              options.refreshAuthentication();
              options.refreshEmbeddedProviders();
            });
          },
        }
      ];

      return (
          <div className={ExplorerStyles.ActionsOnlyCellContainer}>
            <div>
                <AuthenticationActionsMenu
                    actions = {actions}
                    disabled = {actionsMenuDisabled}
                    style = {{}}
                    useIcon = {true}/>
            </div>
          </div>
      );
    }
  });
}
